import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { getLenis } from "../lib/smooth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EASE = [0.215, 0.61, 0.355, 1];

const DELAI = { q: "Pour quand est-ce ?", options: ["Dès que possible", "Sous quelques semaines", "Pas de délai précis"] };

export const QcmModal = ({ service, onClose }) => {
  const questions = [...service.questions, DELAI];
  const [answers, setAnswers] = useState({});
  const [values, setValues] = useState({ nom: "", prenom: "", email: "", telephone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const lenis = getLenis();
    if (lenis) lenis.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      if (lenis) lenis.start();
    };
  }, [onClose]);

  const setAnswer = (qi, option) => {
    setAnswers((a) => ({ ...a, [qi]: option }));
    setErrors((err) => ({ ...err, qcm: undefined }));
  };

  const set = (field) => (e) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
    setErrors((err) => ({ ...err, [field]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = {};
    const missing = questions.some((_, i) => !answers[i]);
    if (missing) err.qcm = "Merci de répondre à toutes les questions.";
    if (!values.nom.trim() || values.nom.trim().length < 2) err.nom = "Veuillez indiquer votre nom.";
    if (!EMAIL_RE.test(values.email)) err.email = "Veuillez indiquer une adresse e-mail valide.";
    setErrors(err);
    if (Object.keys(err).length > 0) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact/qcm`, {
        service: service.title,
        reponses: questions.map((q, i) => ({ question: q.q, choix: answers[i] })),
        ...values,
      });
      toast.success("Votre demande a bien été envoyée. Je vous réponds personnellement.", { duration: 6000 });
      onClose();
    } catch {
      toast.error("Une erreur est survenue. Réessayez ou contactez-moi directement au 06 03 27 46 10.");
    } finally {
      setSending(false);
    }
  };

  const optionCls = (selected) =>
    `text-left text-[14px] px-4 py-3.5 border transition-colors duration-200 ${
      selected ? "bg-ink text-paper border-ink" : "bg-white border-hairline text-ink hover:border-ink"
    }`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onClose}
      data-testid="qcm-overlay"
      className="fixed inset-0 z-[80] bg-ink/45 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        data-testid="qcm-modal"
        className="bg-paper border border-hairline w-full max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-paper border-b border-hairline px-6 md:px-9 py-5 flex items-start justify-between gap-6">
          <div>
            <p className="eyebrow">Prestation choisie</p>
            <h3 className="font-serif text-2xl md:text-3xl tracking-tight mt-2">{service.title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="qcm-close-button"
            aria-label="Fermer le questionnaire"
            className="w-10 h-10 border border-hairline flex items-center justify-center text-soot hover:bg-ink hover:text-paper hover:border-ink transition-colors duration-300 shrink-0"
          >
            <X size={17} />
          </button>
        </div>

        <form onSubmit={onSubmit} noValidate data-testid="qcm-form" className="px-6 md:px-9 py-8 space-y-8">
          {questions.map((q, qi) => (
            <fieldset key={qi}>
              <legend className="field-label flex items-baseline gap-2">
                <span className="text-vermilion">{String(qi + 1).padStart(2, "0")}</span>
                {q.q}
              </legend>
              <div className="grid sm:grid-cols-2 gap-3 mt-1">
                {q.options.map((opt, oi) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setAnswer(qi, opt)}
                    data-testid={`qcm-option-${qi + 1}-${oi + 1}`}
                    aria-pressed={answers[qi] === opt}
                    className={optionCls(answers[qi] === opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </fieldset>
          ))}
          {errors.qcm && <p className="text-xs text-vermilion" data-testid="qcm-error-questions">{errors.qcm}</p>}

          <div className="border-t border-hairline pt-8 grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="qcm-nom" className="field-label">Nom</label>
              <input id="qcm-nom" type="text" value={values.nom} onChange={set("nom")} placeholder="nom" data-testid="qcm-input-nom" className="field" />
              {errors.nom && <p className="mt-2 text-xs text-vermilion" data-testid="qcm-error-nom">{errors.nom}</p>}
            </div>
            <div>
              <label htmlFor="qcm-prenom" className="field-label">Prénom</label>
              <input id="qcm-prenom" type="text" value={values.prenom} onChange={set("prenom")} placeholder="prénom" data-testid="qcm-input-prenom" className="field" />
            </div>
            <div>
              <label htmlFor="qcm-email" className="field-label">E-mail</label>
              <input id="qcm-email" type="email" value={values.email} onChange={set("email")} placeholder="e-mail" data-testid="qcm-input-email" className="field" />
              {errors.email && <p className="mt-2 text-xs text-vermilion" data-testid="qcm-error-email">{errors.email}</p>}
            </div>
            <div>
              <label htmlFor="qcm-tel" className="field-label">Téléphone <span className="normal-case tracking-normal">(optionnel)</span></label>
              <input id="qcm-tel" type="tel" value={values.telephone} onChange={set("telephone")} placeholder="téléphone" data-testid="qcm-input-telephone" className="field" />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="qcm-msg" className="field-label">Un mot pour préciser votre demande <span className="normal-case tracking-normal">(optionnel)</span></label>
              <textarea id="qcm-msg" rows={3} value={values.message} onChange={set("message")} placeholder="Précisions, contexte, délai…" data-testid="qcm-input-message" className="field resize-y" />
            </div>
          </div>

          <button type="submit" disabled={sending} data-testid="qcm-submit-button" className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-wait">
            {sending ? "Envoi en cours…" : "Envoyer"}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};
